import { ModelDetails, Organisation, ORGANISATION_NAME_MAX, ORGANISATION_NAME_MIN } from "./organisation"
import { PASSWORD_MAX, PASSWORD_MIN, User, USER_NAME_MAX, USER_NAME_MIN } from "./user"

export enum ERROR_CODE {
    // General errors
    ERR0 = 0, // Unknown error
    ERR1 = 1, // Exception thrown while trying to update the database
    ERR2 = 2, // Exception thrown while trying to send emails
    ERR3 = 3, // Exception thrown while trying get data from database
    ERR4 = 4, // Security token expired
    ERR5 = 5, // Network error
    ERR6 = 6, // Billing run error
    ERR7 = 7, // Unhandle client error

    // User errors
    ERR100 = 100, // Password must be greater than 8 chars
    ERR101 = 101, // Password does not match confirmation password
    ERR102 = 102, // name is invalid length
    ERR103 = 103, // Cannot find user for email address or password is incorrect
    ERR104 = 104, // Missing or expired token in request
    ERR105 = 105, // Must be linq admin to perform this request
    ERR106 = 106, // Missing or invalid credentials (email or password)
    ERR107 = 107, // User with this email exists
    ERR108 = 108, // Missing email or name in request
    ERR109 = 109, // User is not activated
    ERR110 = 110, // Invalid activation code or it has expired
    ERR111 = 111, // Cannot find user for activation code. may have already activated
    ERR112 = 112, // Cannot find user for user key
    ERR113 = 113, // email is invalid or is invalid length
    ERR114 = 114, // no user key provided
    ERR115 = 115, // exception from calling Freshdesk
    ERR116 = 116, // Reset password requested but user has no password and is not in activation state. So likely used microsoft login
    ERR117 = 117, // User is locked
    ERR118 = 118, // Insights filter does not exist
    ERR119 = 119, // Insights filter already exists

    // Organisation errors
    ERR200 = 200, // organisation name is invalid length
    ERR201 = 201, // no organisation id or invalid organisation id included in request
    ERR202 = 202, // organisation sub has expired
    ERR203 = 203, // user does not have access to this organisation
    ERR204 = 204, // exception while authorising organisation access
    ERR205 = 205, // Must be organisation admin to perform this request
    ERR206 = 206, // Must have organisation write access to perform this request
    ERR207 = 207, // User does not have any access to model
    ERR208 = 208, // User does not have write access to model
    ERR209 = 209, // Missing tag, project or model when updating
    ERR210 = 210, // Missing tag, project or model data when creating
    ERR211 = 211, // No organisation invite for this user
    ERR212 = 212, // User has not activated their invitation
    ERR213 = 213, // Organisation does not match organisation on model
    ERR214 = 214, // Project is missing current state key (current state key)
    ERR215 = 215, // Model must have an administrator
    ERR216 = 216, // Organisation has expired
    ERR217 = 217, // Cannot find model for key
    ERR218 = 218, // Cannot change user access because leaves org with no admins
    ERR219 = 219, // Cannot delete model tag group
    ERR220 = 220, // Tag group key does not match submitted data key
    ERR221 = 221, // Error while updating graph data
    ERR222 = 222, // No backup to restore
    ERR223 = 223, // invalid data for merge nodes
    ERR224 = 224, // Organisation does not have access to AI
    ERR225 = 225, // Model file upload is invalid (syntax error)
    ERR226 = 226, // Emission factor can't be deleted as it is used by nodes
    ERR227 = 227, // Support desk unavailable or returned an error
    ERR228 = 228, // Support ticket details are invalid
    ERR229 = 229, // Information asset details are invalid
    ERR230 = 230, // Emission factor details are invalid
    ERR231 = 231, // Access request is invalid or has no one to send it to
    ERR232 = 232, // Invitation is invalid or the user is already a member
    ERR233 = 233, // Organisation export file is invalid
    ERR234 = 234, // Organisation to import into already has models, projects or nodes
    ERR235 = 235, // Shared node (role, system...) can't be deleted as it's still used, in this or another workspace
    ERR236 = 236, // Current state model can't be replaced by an upload while projects have changed it

    // Graph errors
    ERR300 = 300,
}

export enum HTTP_STATUS {
	OK = 200,
	CREATED = 201,
	DELETED = 204,
	FOUND = 302,
	REDIRECT = 303,
	BAD_REQUEST = 400,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	NOT_FOUND = 404,
	SERVER_ERROR = 500
}

export interface LinqException {
    request?:string,
    client:boolean,
    version:string,
    msgCode:ERROR_CODE,
    component:string,
    service:string,
    internalMsg:string,        
    userKey?:string,
    userEmail?:string,
    organisationKey?:string,
    organisationName?:string,
    modelKey?:string,
    modelName?:string,
    info?:any,
    errorMessage:string,
    errorStack:string,
    httpStatus?:number
}

export const constructLinqException = (client:boolean,
    msgCode:ERROR_CODE,
    component:string,
    service:string,
    internalMsg:string,   
    version: string,
    user?:User, 
    organisation?:Organisation, 
    model?:ModelDetails,
    info?:any,
    error?:any
) => {
    return {
        client,
        version,
        msgCode,
        component,
        service,
        internalMsg,
        userKey:user && user._key,
        userEmail:user && user.email,
        organisationKey: organisation && organisation._key,
        organisationName: organisation && organisation.name,
        modelKey: model && model._key,
        modelName: model && model.name,
        info,
        errorMessage:error?.message,
        errorStack:error?.stack,
        httpStatus:error?.error?.code || error?.status
    } as LinqException
}

export const isLinqException = (error:any) => {
    return error?.msgCode>=0?true:false
}

export const getUserMessage = (linqException:LinqException) => {
    return getUserMessageForCode(linqException)
}

export const getHttpStatus = (linqException:LinqException) => {
    return getHttpStatusForCode(linqException.msgCode)
}

const getHttpStatusForCode = (code:ERROR_CODE) => {
    switch(code) {
        default:
            return HTTP_STATUS.BAD_REQUEST
        case ERROR_CODE.ERR5:     
        case ERROR_CODE.ERR6:     
        case ERROR_CODE.ERR227:
            return HTTP_STATUS.SERVER_ERROR
        case ERROR_CODE.ERR104:     
        case ERROR_CODE.ERR4:   
        case ERROR_CODE.ERR224:
            return HTTP_STATUS.UNAUTHORIZED

        case ERROR_CODE.ERR107:
        case ERROR_CODE.ERR119:
            return HTTP_STATUS.FOUND

        case ERROR_CODE.ERR111:
        case ERROR_CODE.ERR201:
        case ERROR_CODE.ERR209:
        case ERROR_CODE.ERR211:
        case ERROR_CODE.ERR217:
        case ERROR_CODE.ERR118:
            return HTTP_STATUS.NOT_FOUND
        
        case ERROR_CODE.ERR204:
        case ERROR_CODE.ERR3:
        case ERROR_CODE.ERR2:
        case ERROR_CODE.ERR1:
        case ERROR_CODE.ERR0:
        case ERROR_CODE.ERR221:
            return HTTP_STATUS.SERVER_ERROR

        case ERROR_CODE.ERR105:
        case ERROR_CODE.ERR203:
        case ERROR_CODE.ERR205:
        case ERROR_CODE.ERR206:
        case ERROR_CODE.ERR208:        
        case ERROR_CODE.ERR207:
        case ERROR_CODE.ERR216:
        case ERROR_CODE.ERR219:    
        case ERROR_CODE.ERR220:
        case ERROR_CODE.ERR222:
            return HTTP_STATUS.FORBIDDEN
    }
}

const getUserMessageForCode = (linqException:LinqException) => {
    switch(linqException.msgCode) {
        case ERROR_CODE.ERR0:
        default:
            return 'Unknown error occurred. Please try again and if problem persists contact LINQ support.'
        case ERROR_CODE.ERR1:
            return "Error occurred trying to access data. Please try again and if problem persists contact LINQ support."
        case ERROR_CODE.ERR2:
            return "Error occurred trying to send email. Please contact LINQ support."
        case ERROR_CODE.ERR3:
            return "Error occurred trying to retrieve data. Please contact LINQ support."
        case ERROR_CODE.ERR4:
            return "Security token has expired. Logout and login again."
        case ERROR_CODE.ERR5:
            return "Unknown network or server error. Please check your internet connection and try again. If problem persists contact LINQ support."
        case ERROR_CODE.ERR6:
            return "Exception while running billing run."
        case ERROR_CODE.ERR7:
            return "An error has occurred in LINQ. Please refresh the browser and try again. If problem persists contact support."
        case ERROR_CODE.ERR100:
            return `Password must be between ${PASSWORD_MIN} and ${PASSWORD_MAX} characters.`
        case ERROR_CODE.ERR101:
            return 'Password does not match confirmation.'
        case ERROR_CODE.ERR102:
            return `Your name must be between ${USER_NAME_MIN} and ${USER_NAME_MAX} characters.`
        case ERROR_CODE.ERR103:
            return 'Incorrect email or password.'
        case ERROR_CODE.ERR104:
            return 'Invalid or expired token in request.'
        case ERROR_CODE.ERR105:
            return 'You must be a LINQ system admin to perform this request.'
        case ERROR_CODE.ERR106:
            return 'You must enter a valid email and password'
        case ERROR_CODE.ERR107:
            return 'A user with this email exists. Please log in, or register a different email address'
        case ERROR_CODE.ERR108:
            return 'Missing name and or email address in request. Please try again.'
        case ERROR_CODE.ERR109:
            return 'User is not activated. A new email has been sent with a link to activate. Open this email and click on the link.'
        case ERROR_CODE.ERR110:
            return 'Code being used has expired. You will need to send a new code.'
        case ERROR_CODE.ERR111:
            return 'Cannot find a valid user for this activation code or email address does not match. User may have already activated, so try to log in.'
        case ERROR_CODE.ERR112:
            return 'Cannot find user for this key. Please logout and try again.'
        case ERROR_CODE.ERR113:
            return 'Email submitted is not valid. Please change email and try again.'
        case ERROR_CODE.ERR114:
            return 'Request is missing the user key. Please refresh your browser and try again.'
        case ERROR_CODE.ERR115:
            return 'An error occurred while contacting support. Please try again.'
        case ERROR_CODE.ERR116:
            return 'You do not have a password registered. Have you tried logging in using Microsoft?'
        case ERROR_CODE.ERR117:
            return linqException.info?.minutesLeft?`User is locked for another ${linqException.info.minutesLeft} minutes.`:"User is locked"
        case ERROR_CODE.ERR118:
            return 'Insights filter does not exist.'
        case ERROR_CODE.ERR119:
            return 'Insights filter already exists.'
        case ERROR_CODE.ERR200:
            return `Organisation name must be between ${ORGANISATION_NAME_MIN} and ${ORGANISATION_NAME_MAX} characters.`
        case ERROR_CODE.ERR201:
            return 'Cannot find organisation details for this request.'
        case ERROR_CODE.ERR202:
            return 'Organisation has expired. You will need to contact LINQ support to continue with this organisation.'
        case ERROR_CODE.ERR203:
            return 'You do not have access to the organisation requested.'
        case ERROR_CODE.ERR204:
            return 'An error occurred while retrieving organisation details. Try again and if problem persists contact LINQ support.'
        case ERROR_CODE.ERR205:
            return 'You must be a organisation admin to perform this request.'
        case ERROR_CODE.ERR206:
            return 'You must have write access for this organisation to perform this request.'
        case ERROR_CODE.ERR207:
            return 'You do not have access to this model'
        case ERROR_CODE.ERR208:
            return 'You do not have write access to this model'
        case ERROR_CODE.ERR209:
            return 'This model, project or tag does not exist. Please refresh your browser and try again.'
        case ERROR_CODE.ERR210:
            return 'Missing data for creating model, project or tag. Please refresh your browser and try again.'
        case ERROR_CODE.ERR211:
            return 'There is no invite to this organisation for you. Please contact the organisation asministrator and ask them to invite you.'
        case ERROR_CODE.ERR212:
            return 'You need to activate your invitaton to this organisation by selecting the organisation menu.'
        case ERROR_CODE.ERR213:
            return 'Mismatch between organisation and the organisation identified for this model.'
        case ERROR_CODE.ERR214:
            return 'Must have selected a model when creating a new project. Please try again.'
        case ERROR_CODE.ERR215:
            return 'Model must have an administrator.'
        case ERROR_CODE.ERR216:
            return 'Organisation has expired. Please contact LINQ to extend access.'
        case ERROR_CODE.ERR217:
            return 'Unable to find model for this key.'
        case ERROR_CODE.ERR218:
            return 'Cannot change user access to organisation as would leave organisation with no administrator'
        case ERROR_CODE.ERR219:
            return 'Cannot remove the organisation model tag group'
        case ERROR_CODE.ERR220:
            return 'Key in submission does not match data. Please refresh and try again'
        case ERROR_CODE.ERR221:
            return 'A problem occured while trying to update the model. Please try again and if problem persists contact support.'
        case ERROR_CODE.ERR222:
            return 'Unable to locate backup or backup data to restore.'
        case ERROR_CODE.ERR223:
            return 'Invalid data submitted to merge nodes. Must have a valid to node and list of from nodes.'
        case ERROR_CODE.ERR224:
            return 'Organisation is not licensed to use AI. Contact Linq to upgrade your license.'
        case ERROR_CODE.ERR225:
            return 'Syntax error in file. Please check content and try again.'
        case ERROR_CODE.ERR226:
            return emissionFactorInUseMessage(linqException.info?.usage)
        case ERROR_CODE.ERR235:
            return sharedNodeInUseMessage(linqException.info?.usage)
        case ERROR_CODE.ERR236:
            return modelChangedInProjectsMessage(linqException.info?.projects)
        case ERROR_CODE.ERR227:
            return "The support desk couldn't be reached. Please try again later, or email LINQ support."
        case ERROR_CODE.ERR233:
            return linqException.info?.problem ? `The organisation wasn't imported: ${linqException.info.problem}` : "The organisation export file can't be imported."
        case ERROR_CODE.ERR234:
            return "The organisation already has models, projects or nodes. Import into an empty organisation, or as a new one."
        case ERROR_CODE.ERR232:
            return linqException.info?.problem ? `The invitation wasn't sent: ${linqException.info.problem}.` : "The invitation couldn't be sent."
        case ERROR_CODE.ERR231:
            return linqException.info?.problem ? `The request wasn't sent: ${linqException.info.problem}.` : "The access request couldn't be sent."
        case ERROR_CODE.ERR230:
            return linqException.info?.problem ? `The emission factors weren't saved: ${linqException.info.problem}.` : "The emission factor details are invalid."
        case ERROR_CODE.ERR229:
            return linqException.info?.problem ? `The information asset wasn't saved: ${linqException.info.problem}.` : "The information asset details are invalid."
        case ERROR_CODE.ERR228:
            return linqException.info?.problem ? `The support ticket wasn't sent: ${linqException.info.problem}.` : "The support ticket details are incomplete."
    }

}

// info.usage lists where each factor is used: { factor, node, model, workspace }
// where each shared node being deleted is still used, so it can be removed there first
const sharedNodeInUseMessage = (usage?:{node?:string, model?:string, workspace?:string}[]) => {
    if (!usage?.length) return "The changes weren't saved: what you deleted is still used elsewhere."
    const where = (u:{model?:string, workspace?:string}) => `${u.model || 'a model'} in ${u.workspace === 'current state' ? 'current state' : `the project ${u.workspace || ''}`}`
    const shown = usage.slice(0, 5).map(u => `${u.node || 'A node'} is still used in ${where(u)}`)
    const more = usage.length > shown.length ? ` and ${usage.length - shown.length} more` : ''
    return `The changes weren't saved, because what you deleted is still used elsewhere: ${shown.join('; ')}${more}. Remove it there first, then delete it here.`
}

const modelChangedInProjectsMessage = (projects?:string[]) => {
    if (!projects?.length) return "The model wasn't uploaded, because projects have changed it. Reset it in those projects to current state first, then upload."
    const names = projects.map(p => `"${p}"`).join(', ')
    const one = projects.length === 1
    return `The model wasn't uploaded, because ${one ? 'the project' : 'the projects'} ${names} ${one ? 'has' : 'have'} changed it, and ${one ? 'its' : 'their'} changes are built on the current state nodes an upload replaces. Reset the model in ${one ? 'that project' : 'those projects'} to current state, then upload.`
}

const emissionFactorInUseMessage = (usage?:{factor:string, node:string, model:string, workspace:string}[]) => {
    if (!usage?.length) return "The emission factor is still in use, so it can't be deleted."
    const shown = usage.slice(0, 5).map(u => `${u.factor} is used by ${u.node || 'an item'} in ${u.model || 'a model'} (${u.workspace})`)
    const more = usage.length > shown.length ? ` and ${usage.length - shown.length} more` : ''
    return `Emission factors that are in use can't be deleted: ${shown.join('; ')}${more}. Remove them from those items first.`
}
